import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle
} from "@/components/ui/dialog";
import { RefreshCw, Trash2, Eye, Search } from "lucide-react";

const STATUS_OPTIONS = [
  { value: "__all__", label: "tümü" },
  { value: "pending", label: "pending" },
  { value: "running", label: "running" },
  { value: "retry", label: "retry" },
  { value: "failed", label: "failed" },
  { value: "done", label: "done" },
];

function StatusBadge({ status }) {
  const map = {
    pending: "info",
    running: "info",
    retry: "warn",
    failed: "err",
    done: "ok",
  };
  return <span className={`status-badge ${map[status] || "muted"}`}>{status}</span>;
}

export default function Queue() {
  const [rows, setRows] = useState([]);
  const [statusFilter, setStatusFilter] = useState("__all__");
  const [domainFilter, setDomainFilter] = useState("");
  const [detail, setDetail] = useState(null);

  const load = async () => {
    try {
      const params = {};
      if (statusFilter !== "__all__") params.status = statusFilter;
      if (domainFilter) params.domain = domainFilter;
      const { data } = await api.get("/queue", { params });
      setRows(Array.isArray(data) ? data : []);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  useEffect(() => {
    load();
    const iv = setInterval(load, 5000);
    return () => clearInterval(iv);
  }, [statusFilter, domainFilter]);

  const retry = async (id) => {
    try {
      await api.post(`/queue/${id}/retry`);
      toast.success("Yeniden deneniyor");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Bu işi kuyruğtan sil?")) return;
    try {
      await api.delete(`/queue/${id}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const clearFailed = async () => {
    if (!window.confirm("Tüm 'failed' işler silinsin mi?")) return;
    try {
      await api.post("/queue/clear-failed");
      toast.success("Temizlendi");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Senkron Kuyruğu</h1>
          <p className="text-sm text-muted-foreground mt-1">DNS senkron işleri · gerçek zamanlı</p>
        </div>
        <div className="flex gap-2">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-36" data-testid="queue-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              {STATUS_OPTIONS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input placeholder="domain ara..." value={domainFilter} onChange={(e) => setDomainFilter(e.target.value)} className="pl-8 w-56" />
          </div>
          <Button variant="secondary" onClick={clearFailed} data-testid="queue-clear-failed">Başarısızları Temizle</Button>
        </div>
      </div>

      <div className="border border-border bg-card rounded-md overflow-hidden">
        <table className="data-table" data-testid="queue-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Domain</th>
              <th>Action</th>
              <th>Hedef PDNS</th>
              <th>Durum</th>
              <th>Retry</th>
              <th>Sonraki</th>
              <th>Oluşturulma</th>
              <th className="text-right">İşlem</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={9} className="text-center py-10 text-muted-foreground text-sm font-mono">kuyruk boş</td></tr>
            )}
            {rows.map((j) => (
              <tr key={j.id} data-testid={`queue-row-${j.id}`}>
                <td className="font-mono text-[10px] text-muted-foreground">#{j.id}</td>
                <td className="font-mono text-xs">{j.domain}</td>
                <td><span className="status-badge info">{j.action}</span></td>
                <td className="font-mono text-xs">{j.target_pdns_id || "—"}</td>
                <td><StatusBadge status={j.status} /></td>
                <td className="font-mono text-xs">{j.retry_count}</td>
                <td className="font-mono text-xs text-muted-foreground">{j.next_retry_at ? new Date(j.next_retry_at).toLocaleString("tr-TR") : "—"}</td>
                <td className="font-mono text-xs text-muted-foreground">{new Date(j.created_at).toLocaleString("tr-TR")}</td>
                <td>
                  <div className="flex justify-end gap-1">
                    <Button size="sm" variant="ghost" onClick={() => setDetail(j)} title="Detay"><Eye size={14} /></Button>
                    <Button size="sm" variant="ghost" onClick={() => retry(j.id)} title="Yeniden dene" data-testid={`queue-retry-${j.id}`}><RefreshCw size={14} /></Button>
                    <Button size="sm" variant="ghost" onClick={() => remove(j.id)} className="text-red-400 hover:text-red-300" title="Sil"><Trash2 size={14} /></Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <DialogContent data-testid="queue-detail-dialog">
          <DialogHeader>
            <DialogTitle>İş #{detail?.id} Detay</DialogTitle>
          </DialogHeader>
          {detail && (
            <div className="space-y-2 text-sm">
              <div><span className="text-muted-foreground text-xs uppercase tracking-wider font-mono">Domain:</span> <span className="font-mono">{detail.domain}</span></div>
              <div><span className="text-muted-foreground text-xs uppercase tracking-wider font-mono">Action:</span> <span className="font-mono">{detail.action}</span></div>
              <div><span className="text-muted-foreground text-xs uppercase tracking-wider font-mono">Durum:</span> <StatusBadge status={detail.status} /></div>
              <div><span className="text-muted-foreground text-xs uppercase tracking-wider font-mono">Retry:</span> <span className="font-mono">{detail.retry_count}</span></div>
              {detail.last_error && (
                <div>
                  <div className="text-muted-foreground text-xs uppercase tracking-wider font-mono mb-1">Son Hata:</div>
                  <pre className="bg-background border border-border rounded p-3 text-xs font-mono text-red-400 whitespace-pre-wrap overflow-x-auto">{detail.last_error}</pre>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
